import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Wallet, Clock, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/format";

const METHODS = [
  { key: "stripe", label: "Stripe (bank)" },
  { key: "paypal", label: "PayPal" },
  { key: "venmo", label: "Venmo" },
];

export default function PartnerLedgerCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["enh-ledger"], queryFn: () => api.get("/enhancer/me/ledger").then((r) => r.data) });
  const [method, setMethod] = useState("stripe");
  const [paypal, setPaypal] = useState("");
  const [venmo, setVenmo] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) { setMethod(data.payout_method || "stripe"); setPaypal(data.paypal_email || ""); setVenmo(data.venmo_handle || ""); }
  }, [data]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/enhancer/me/payout-method", { method, paypal_email: paypal, venmo_handle: venmo });
      toast.success("Payout preference saved.");
      qc.invalidateQueries({ queryKey: ["enh-ledger"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (isLoading) return <div className="eyw-card p-5 mb-6"><Loader2 className="animate-spin text-[#144F75]" size={18} /></div>;

  return (
    <div className="eyw-card p-5 mb-6" data-testid="partner-ledger-card">
      <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-4"><Wallet size={18} className="text-[#144F75]" /> Ledger &amp; balance</h3>

      {data?.payout_hold && (
        <div className="mb-4 rounded-xl bg-[#B45151]/[0.08] border border-[#B45151]/30 px-4 py-3 text-sm text-[#B45151] flex items-start gap-2" data-testid="ledger-hold-banner">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" /> Payouts are on hold due to a refund/chargeback clawback. New earnings will first clear the negative balance.
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="rounded-xl bg-[#F9F7F3] px-4 py-3" data-testid="ledger-available"><p className="text-[11px] uppercase tracking-wide text-[#5C8A72]">Available</p><p className="font-serif text-2xl text-[#5C8A72]">{money((data?.available ?? 0))}</p></div>
        <div className="rounded-xl bg-[#D98E73]/[0.1] px-4 py-3" data-testid="ledger-held"><p className="text-[11px] uppercase tracking-wide text-[#B5794E] flex items-center gap-1"><Clock size={11} /> Held</p><p className="font-serif text-2xl text-[#B5794E]">${(data?.held ?? 0).toFixed(2)}</p></div>
        <div className="rounded-xl bg-[#144F75]/[0.06] px-4 py-3" data-testid="ledger-paid"><p className="text-[11px] uppercase tracking-wide text-[#144F75]">Paid out</p><p className="font-serif text-2xl text-[#144F75]">{money((data?.paid ?? 0))}</p></div>
        <div className="rounded-xl bg-[#F9F7F3] px-4 py-3" data-testid="ledger-clawback"><p className="text-[11px] uppercase tracking-wide text-[#8FA89B]">Clawbacks</p><p className="font-serif text-2xl text-[#4A5D6B]">{money((data?.clawback ?? 0))}</p></div>
      </div>

      <p className="text-xs text-[#8FA89B] mb-4 flex items-center gap-1.5"><CheckCircle2 size={13} className="text-[#5C8A72]" /> Held earnings release automatically 24h after a session ends (or 5 days after a product ships).</p>

      {/* Payout method preference */}
      <div className="border-t border-[#E5E0D8] pt-4">
        <p className="text-sm font-semibold text-[#1A242B] mb-2">Preferred payout method</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {METHODS.map((m) => (
            <button key={m.key} onClick={() => setMethod(m.key)} data-testid={`payout-method-${m.key}`}
              className={`px-3 py-1.5 rounded-full text-sm ${method === m.key ? "bg-[#144F75] text-white" : "bg-[#F1EEE8] text-[#4A5D6B]"}`}>{m.label}</button>
          ))}
        </div>
        {method === "paypal" && <input placeholder="PayPal email" value={paypal} onChange={(e) => setPaypal(e.target.value)} data-testid="payout-paypal-email" className="eyw-input w-full mb-2" />}
        {method === "venmo" && <input placeholder="@venmo-handle" value={venmo} onChange={(e) => setVenmo(e.target.value)} data-testid="payout-venmo-handle" className="eyw-input w-full mb-2" />}
        {method !== "stripe" && <p className="text-[11px] text-[#B5794E] mb-2">PayPal &amp; Venmo payouts are being enabled — your preference is saved and will be used once live. Stripe bank transfers are active now.</p>}
        <button onClick={save} disabled={saving} data-testid="payout-method-save" className="eyw-btn-ghost !py-2 text-sm">{saving ? <Loader2 className="animate-spin" size={14} /> : null} Save preference</button>
      </div>
    </div>
  );
}
